import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ListSection from "./ListSection";
import { RejectIcon, PendingIcon, BulkOfferIcon, ApprovedUser } from "../../../assets/icons";
import { fetchPendingVerifications, fetchCompanyStats } from "../../../redux/slice/companySlice";
import { TabCard } from "../../../components/custom-card/Cards";

const VerificationRequests = () => {
    const dispatch = useDispatch();
    const { pendingVerifications, pendingVerificationsLoading, pendingVerificationsMeta, companyStats } = useSelector(state => state.company);

    const [searchParams, setSearchParams] = useSearchParams();

    // Read from URL or use defaults
    const cardStatus = searchParams.get("status") || "";
    const currentPage = parseInt(searchParams.get("page") || "1");
    const [localSearchTerm, setLocalSearchTerm] = useState(searchParams.get("search") || "");
    const [debouncedSearch, setDebouncedSearch] = useState(localSearchTerm);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(localSearchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [localSearchTerm]);

    useEffect(() => {
        const currentSearchParam = searchParams.get("search") || "";
        if (debouncedSearch !== currentSearchParam) {
            const currentParams = Object.fromEntries(searchParams.entries());
            if (debouncedSearch) {
                setSearchParams({ ...currentParams, search: debouncedSearch, page: 1 });
            } else {
                delete currentParams.search;
                setSearchParams({ ...currentParams, page: 1 });
            }
        }
    }, [debouncedSearch, searchParams, setSearchParams]);

    const searchTerm = searchParams.get("search") || "";
    const [limit, setLimit] = useState({
        value: parseInt(searchParams.get("limit") || "10"),
        label: parseInt(searchParams.get("limit") || "10")
    });

    const totalPending = pendingVerificationsMeta?.total || 0;

    useEffect(() => {
        dispatch(fetchCompanyStats());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchPendingVerifications({
            page: currentPage,
            limit: limit.value,
            search: searchTerm,
            status: cardStatus
        }));
    }, [dispatch, currentPage, limit.value, searchTerm, cardStatus]);

    const cards = [
        { id: 1, total: companyStats?.total_verifications || 0, title: "Total Verification Requests", icon: <BulkOfferIcon size={24} />, iconClass: "icon-primary", status: "", },
        { id: 2, total: companyStats?.verification_requests || 0, title: "Verification Request", icon: <PendingIcon size={24} />, iconClass: "icon-warning", status: "submitted", },
        { id: 3, total: companyStats?.approved_verifications || 0, title: "Approved Request", icon: <ApprovedUser size={24} />, iconClass: "icon-success", status: "approved", },
        { id: 4, total: companyStats?.rejected_verifications || 0, title: "Rejected Request", icon: <RejectIcon size={24} />, iconClass: "icon-danger", status: "rejected", },
    ];
    const updateParams = (newParams) => {
        const current = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...current, ...newParams });
    };

    const handleCardClick = (status) => {
        updateParams({ status, page: 1 });
    };

    const handleLimitChange = (option) => {
        if (!option) return;
        setLimit(option);
        updateParams({ limit: option.value, page: 1 });
    };

    const handlePageChange = (newPageNumber) => {
        updateParams({ page: newPageNumber });
    };

    const paginationConfig = {
        currentPage,
        totalPages: Math.ceil(totalPending / limit.value) || 1,
        total: totalPending,
        onPageChange: handlePageChange,
        pageSize: limit.value,
        setPageSize: (size) => handleLimitChange({ value: size, label: size }),
        setCurrentPage: handlePageChange,
    };

    return (
        <Container fluid className="px-0">
            <div className="grid gap-4">
                {cards.map((item, i) => (
                    <TabCard
                        key={i}
                        count={item.total}
                        title={item.title}
                        className="g-col-4 g-col-xxl-3"
                        icon={item.icon}
                        active={item.status === cardStatus}
                        iconClassName={item.iconClass}
                        onClick={() => handleCardClick(item.status)}
                    />
                ))}
            </div>

            <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
                <ListSection
                    searchTerm={localSearchTerm}
                    setSearchTerm={setLocalSearchTerm}
                    cardStatus={cardStatus}
                    data={pendingVerifications}
                    isLoading={pendingVerificationsLoading}
                    pagination={paginationConfig}
                />
            </div>
        </Container>
    );
};

export default VerificationRequests;
